/**
 * Custom error class for service layer errors with consistent structure
 */
export class ServiceError extends Error {
  public readonly code?: string;
  public readonly statusCode?: number;
  public readonly details?: any;
  public readonly isRetryable: boolean;
  public readonly timestamp: Date;

  constructor(
    message: string,
    options?: {
      code?: string;
      statusCode?: number;
      details?: any;
      isRetryable?: boolean;
      cause?: Error;
    }
  ) {
    super(message);
    this.name = 'ServiceError';
    this.code = options?.code;
    this.statusCode = options?.statusCode;
    this.details = options?.details;
    this.isRetryable = options?.isRetryable ?? ServiceError.shouldRetry(options?.statusCode);
    this.timestamp = new Date();

    // Maintain proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError);
    }

    // Include the cause if provided
    if (options?.cause) {
      this.cause = options.cause;
    }
  }

  /**
   * Determine if error is retryable based on status code
   */
  private static shouldRetry(statusCode?: number): boolean {
    if (!statusCode) return false;
    
    // Retry on network errors and server errors
    return (
      statusCode === 408 || // Request Timeout
      statusCode === 429 || // Too Many Requests
      statusCode === 502 || // Bad Gateway
      statusCode === 503 || // Service Unavailable
      statusCode === 504    // Gateway Timeout
    );
  }

  /**
   * Convert error to plain object for serialization
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details,
      isRetryable: this.isRetryable,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }

  /**
   * Create ServiceError from axios or fetch error
   */
  static fromHttpError(error: any): ServiceError {
    // Handle axios errors
    if (error.response) {
      const { status, data } = error.response;
      return new ServiceError(
        data?.message || data?.error || `HTTP ${status} Error`,
        {
          statusCode: status,
          code: data?.code || `HTTP_${status}`,
          details: data,
          cause: error
        }
      );
    }

    // Handle network errors
    if (error.request) {
      return new ServiceError(
        'Network error: Unable to reach the server',
        {
          code: 'NETWORK_ERROR',
          isRetryable: true,
          cause: error
        }
      );
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return new ServiceError(
        'Request timeout: The server took too long to respond',
        {
          code: 'TIMEOUT',
          statusCode: 408,
          isRetryable: true,
          cause: error
        }
      );
    }

    // Handle other errors
    return new ServiceError(
      error.message || 'An unexpected error occurred',
      {
        code: 'UNKNOWN_ERROR',
        cause: error
      }
    );
  }

  /**
   * Create ServiceError from validation errors
   */
  static validation(message: string, fieldErrors?: Record<string, string>): ServiceError {
    return new ServiceError(message, {
      code: 'VALIDATION_ERROR',
      statusCode: 400,
      details: fieldErrors,
      isRetryable: false
    });
  }

  /**
   * Create ServiceError for authentication failures
   */
  static unauthorized(message: string = 'Authentication required', details?: any): ServiceError {
    return new ServiceError(message, {
      code: 'UNAUTHORIZED',
      statusCode: 401,
      isRetryable: false,
      details
    });
  }

  /**
   * Create ServiceError for permission failures
   */
  static forbidden(message: string = 'Access denied'): ServiceError {
    return new ServiceError(message, {
      code: 'FORBIDDEN',
      statusCode: 403,
      isRetryable: false
    });
  }

  /**
   * Create ServiceError for not found resources
   */
  static notFound(resource: string): ServiceError {
    return new ServiceError(`${resource} not found`, {
      code: 'NOT_FOUND',
      statusCode: 404,
      isRetryable: false
    });
  }

  /**
   * Create ServiceError for conflicts
   */
  static conflict(message: string): ServiceError {
    return new ServiceError(message, {
      code: 'CONFLICT',
      statusCode: 409,
      isRetryable: false
    });
  }

  /**
   * Create ServiceError for rate limiting
   */
  static rateLimited(retryAfter?: number): ServiceError {
    return new ServiceError('Too many requests. Please try again later.', {
      code: 'RATE_LIMITED',
      statusCode: 429,
      details: { retryAfter },
      isRetryable: true
    });
  }

  /**
   * Create ServiceError for server errors
   */
  static serverError(message: string = 'Internal server error'): ServiceError {
    return new ServiceError(message, {
      code: 'SERVER_ERROR',
      statusCode: 500,
      isRetryable: true
    });
  }
}